import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import './Reports.css';
import { companiesAPI, depotsAPI, reportsAPI, usersAPI, devicesAPI } from '../services/api';
import { normalizeBatches } from '../utils/batches';
import DataTable from '../components/DataTable';
import { useAuthStore } from '../store/authStore';
import { ExportCsvIcon, ExportExcelIcon } from '../components/Icons';

export default function Reports() {
  const { user } = useAuthStore();
  const activeReportRequestRef = useRef(0);
  const selectedReportTypeRef = useRef('seals_assigned');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportType, setReportType] = useState('seals_assigned');
  const [reloadToken, setReloadToken] = useState(0);
  
  // Data
  const [reportData, setReportData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryReport, setSummaryReport] = useState(null);
  const [globalInventory, setGlobalInventory] = useState(null);
  
  // Filters
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedDepots, setSelectedDepots] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [bagFilter, setBagFilter] = useState('');
  const [sealFilter, setSealFilter] = useState('');
  const [selectedTracingUserIds, setSelectedTracingUserIds] = useState([]);
  const [containerNumber, setContainerNumber] = useState('');
  const [selectedLoadedUserIds, setSelectedLoadedUserIds] = useState([]);
  const [sealsLoadedCompanyId, setSealsLoadedCompanyId] = useState('');
  const [clientSealsStatus, setClientSealsStatus] = useState('');

  useEffect(() => {
    if (!user) return;

    fetchCompanies();
    fetchDepots();
    fetchUsers();

    const initialType = user.user_type === 'cliente'
      ? 'client_seals_received'
      : 'seals_assigned';

    setReportType(initialType);
  }, [user]);

  useEffect(() => {
    selectedReportTypeRef.current = reportType;
  }, [reportType]);

  useEffect(() => {
    if (!user) return;
    if (user.user_type === 'cliente' && reportType === 'seals_assigned') return;
    resetReportOutput();
    loadReportForType(reportType, 1);
  }, [user, reportType, reloadToken]);

  // Fetch batches when company selection or user changes
  useEffect(() => {
    if (!user) return;
    let companyId = null;
    if (user.user_type === 'cliente') {
      companyId = user.company_id;
    } else if (selectedCompanies.length === 1) {
      companyId = selectedCompanies[0];
    }
    if (companyId) {
      fetchBatches(companyId);
    } else {
      setBatches([]);
    }
    setSelectedBatchId('');
  }, [user, selectedCompanies]);

  const role = user?.user_type;

  const operadorAssignedDepotIds = useMemo(() => {
    if (!user) return [];
    const assigned = user.depot_ids || (user.depot_id ? [user.depot_id] : []);
    return (assigned || []).map((id) => Number(id));
  }, [user]);

  const visibleDepots = useMemo(() => {
    if (selectedCompanies.length === 0) return depots;
    const companySet = new Set(selectedCompanies.map((id) => Number(id)));
    return depots.filter((depot) => companySet.has(Number(depot.company_id)));
  }, [depots, selectedCompanies]);

  const availableReportOptions = useMemo(() => {
    const options = [];

    if (!role) return options;

    if (role === 'gods_eye') {
      options.push(
        { value: 'seals_loaded', label: 'Seals Loaded to System' },
        { value: 'seals_tracing', label: 'Seals Tracing' },
        { value: 'global_inventory', label: 'Global Inventory' },
      );
    }

    if (role === 'gods_eye' || role === 'manager') {
      options.push(
        { value: 'seals_assigned', label: 'Seals Assigned (Boxes to Depots)' },
        { value: 'summary', label: 'Summary Overview' },
      );
    }

    if (role === 'cliente' || role === 'gods_eye' || role === 'manager') {
      options.push(
        { value: 'client_seals_received', label: 'Client - Seals Received' },
        { value: 'client_seals_assigned_depots', label: 'Client - Seals Assigned to Depots' },
      );
    }

    if (role === 'cliente' || role === 'operador' || role === 'gods_eye' || role === 'manager') {
      options.push({ value: 'seals_used', label: 'Seals Used / Assigned to Container' });
    }

    if (role === 'distribuidor' || role === 'gods_eye' || role === 'manager') {
      options.push(
        { value: 'distributor_boxes_assigned', label: 'Distributor - Boxes Assigned' },
        { value: 'distributor_inventory_status', label: 'Distributor - Inventory Status' },
      );
    }

    // Fallback for any role that still has reports access but no specific mapping
    if (options.length === 0) {
      options.push(
        { value: 'seals_assigned', label: 'Seals Assigned (Boxes to Depots)' },
        { value: 'summary', label: 'Summary Overview' },
      );
    }

    return options;
  }, [role]);

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getPublic();
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Error fetching companies');
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await depotsAPI.getPublic();
      let depotList = response.data || [];

      // If operador, restrict depots to those assigned to the operador
      if (user?.user_type === 'operador') {
        const assigned = user.depot_ids || (user.depot_id ? [user.depot_id] : []);
        if (assigned && assigned.length > 0) {
          const assignedSet = new Set(assigned.map((id) => Number(id)));
          depotList = depotList.filter((d) => assignedSet.has(Number(d.id)));
          // Preselect operador's depot(s)
          setSelectedDepots(assigned.map((id) => Number(id)));
        } else {
          // No assigned depots
          depotList = [];
          setSelectedDepots([]);
        }
      }

      setDepots(depotList);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Do not toast here to avoid noise; filters still work without user list
    }
  };

  const fetchBatches = async (companyId) => {
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      const resp = await devicesAPI.getBatches(params);
      setBatches(normalizeBatches(resp.data || []));
    } catch (err) {
      console.error('Error fetching batches', err);
      setBatches([]);
    }
  };

  const resetReportOutput = () => {
    activeReportRequestRef.current += 1;
    setLoading(false);
    setReportData([]);
    setTotalCount(0);
    setCurrentPage(1);
    setSummaryReport(null);
    setGlobalInventory(null);
  };

  const beginReportLoad = (expectedType) => {
    const requestId = activeReportRequestRef.current + 1;
    activeReportRequestRef.current = requestId;
    setLoading(true);
    setReportData([]);
    setTotalCount(0);
    setSummaryReport(null);
    setGlobalInventory(null);
    return { requestId, expectedType };
  };

  const canApplyReportLoad = ({ requestId, expectedType }) => (
    requestId === activeReportRequestRef.current &&
    expectedType === selectedReportTypeRef.current
  );

  const finishReportLoad = (loadState) => {
    if (canApplyReportLoad(loadState)) {
      setLoading(false);
    }
  };

  const loadSealsReport = async (page = 1) => {
    const loadState = beginReportLoad('seals_assigned');
    try {
      const skip = (page - 1) * itemsPerPage;
      
      // Build company IDs string
      let companyIds = selectedCompanies.length > 0 ? selectedCompanies.join(',') : null;
      let depotIds = selectedDepots.length > 0 ? selectedDepots.join(',') : null;

      // If operador, always restrict to assigned depot(s) and ignore company filter
      if (role === 'operador') {
        companyIds = null;
        if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
          depotIds = operadorAssignedDepotIds.join(',');
        } else {
          depotIds = null;
        }
      }
      
      const response = await reportsAPI.getSealsAssignedData({
        company_ids: companyIds,
        depot_ids: depotIds,
        start_date: startDate || null,
        end_date: endDate || null,
        upload_batch_id: selectedBatchId || null,
        skip,
        limit: itemsPerPage,
      });
      if (!canApplyReportLoad(loadState)) return;
      
      // Axios returns { data: { data: [...], total: number } }
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading report:', error);
      toast.error('Error loading report data');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadSummaryReport = async () => {
    const loadState = beginReportLoad('summary');
    try {
      const response = await reportsAPI.getSummary(startDate || null, endDate || null);
      if (!canApplyReportLoad(loadState)) return;
      setSummaryReport(response.data || null);
      setReportData([]);
      setTotalCount(0);
      setCurrentPage(1);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading summary report:', error);
      toast.error('Error loading summary report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadSealsLoadedReport = async (page = 1) => {
    const loadState = beginReportLoad('seals_loaded');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        skip,
        limit: itemsPerPage,
      };
      if (selectedLoadedUserIds.length > 0) {
        params.user_ids = selectedLoadedUserIds.join(',');
      }
      // If operador, do not allow company filter; instead restrict to assigned depot(s) if possible
      if (role === 'operador') {
        if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
          params.depot_ids = operadorAssignedDepotIds.join(',');
        }
      } else if (sealsLoadedCompanyId) {
        params.company_id = sealsLoadedCompanyId;
      }
      if (selectedBatchId) params.upload_batch_id = selectedBatchId;
      const response = await reportsAPI.getSealsLoaded(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading seals loaded report:', error);
      toast.error('Error loading Seals Loaded report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadSealsTracingReport = async (page = 1) => {
    const loadState = beginReportLoad('seals_tracing');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        skip,
        limit: itemsPerPage,
      };
      if (bagFilter) params.bag = bagFilter;
      if (sealFilter) params.seal = sealFilter;
      if (selectedTracingUserIds.length > 0) {
        params.user_ids = selectedTracingUserIds.join(',');
      }
      if (selectedBatchId) params.upload_batch_id = selectedBatchId;
      const response = await reportsAPI.getSealsTracing(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading seals tracing report:', error);
      toast.error('Error loading Seals Tracing report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadGlobalInventoryReport = async () => {
    const loadState = beginReportLoad('global_inventory');
    try {
      const response = await reportsAPI.getGlobalInventory({
        company_ids: selectedCompanies.length > 0 ? selectedCompanies.join(',') : null,
      });
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setGlobalInventory(payload.data || []);
      setReportData([]);
      setTotalCount(payload.total || 0);
      setCurrentPage(1);
      setSummaryReport(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading global inventory report:', error);
      toast.error('Error loading Global Inventory report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadClientSealsReceivedReport = async (page = 1) => {
    const loadState = beginReportLoad('client_seals_received');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        start_date: startDate || null,
        end_date: endDate || null,
        skip,
        limit: itemsPerPage,
      };
      if (selectedCompanies.length > 0) {
        params.company_ids = selectedCompanies.join(',');
      }
      if (selectedBatchId) {
        params.upload_batch_id = selectedBatchId;
      }
      // If operador, force depot_ids to assigned depots; otherwise use selectedDepots
      if (role === 'operador') {
        if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
          params.depot_ids = operadorAssignedDepotIds.join(',');
        }
      } else if (selectedDepots.length > 0) {
        params.depot_ids = selectedDepots.join(',');
      }
      if (clientSealsStatus) {
        params.status = clientSealsStatus;
      }
      const response = await reportsAPI.getClientSealsReceived(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading client seals received report:', error);
      toast.error('Error loading Seals Received report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadClientSealsAssignedDepotsReport = async (page = 1) => {
    const loadState = beginReportLoad('client_seals_assigned_depots');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        start_date: startDate || null,
        end_date: endDate || null,
        skip,
        limit: itemsPerPage,
      };
      // Ensure operador only sees their assigned depot
      if (role === 'operador') {
        if (operadorAssignedDepotIds && operadorAssignedDepotIds.length === 1) {
              params.depot_id = operadorAssignedDepotIds[0];
        }
      } else if (selectedDepots.length === 1) {
        params.depot_id = selectedDepots[0];
      }
      const response = await reportsAPI.getClientSealsAssignedDepots(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading client seals assigned depots report:', error);
      toast.error('Error loading Seals Assigned to Depots report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadSealsUsedReport = async (page = 1) => {
    const loadState = beginReportLoad('seals_used');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        start_date: startDate || null,
        end_date: endDate || null,
        skip,
        limit: itemsPerPage,
      };
      if (role === 'operador') {
        if (operadorAssignedDepotIds && operadorAssignedDepotIds.length === 1) {
              params.depot_id = operadorAssignedDepotIds[0];
        }
      } else if (selectedDepots.length === 1) {
        params.depot_id = selectedDepots[0];
      }
      if (containerNumber) {
        params.container_number = containerNumber;
      }
      if (selectedBatchId) params.upload_batch_id = selectedBatchId;
      const response = await reportsAPI.getSealsUsed(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading seals used report:', error);
      toast.error('Error loading Seals Used report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadDistributorBoxesAssignedReport = async (page = 1) => {
    const loadState = beginReportLoad('distributor_boxes_assigned');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        start_date: startDate || null,
        end_date: endDate || null,
        skip,
        limit: itemsPerPage,
      };
      if (selectedCompanies.length > 0) {
        params.company_ids = selectedCompanies.join(',');
      }
      const response = await reportsAPI.getDistributorBoxesAssigned(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading distributor boxes assigned report:', error);
      toast.error('Error loading Boxes Assigned report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadDistributorInventoryStatusReport = async (page = 1) => {
    const loadState = beginReportLoad('distributor_inventory_status');
    try {
      const skip = (page - 1) * itemsPerPage;
      const params = {
        start_date: startDate || null,
        end_date: endDate || null,
        skip,
        limit: itemsPerPage,
      };
      if (selectedCompanies.length > 0) {
        params.company_ids = selectedCompanies.join(',');
      }
      const response = await reportsAPI.getDistributorInventoryStatus(params);
      if (!canApplyReportLoad(loadState)) return;
      const payload = response.data || {};
      setReportData(payload.data || []);
      setTotalCount(payload.total || 0);
      setCurrentPage(page);
      setSummaryReport(null);
      setGlobalInventory(null);
    } catch (error) {
      if (!canApplyReportLoad(loadState)) return;
      console.error('Error loading distributor inventory status report:', error);
      toast.error('Error loading Inventory Status report');
    } finally {
      finishReportLoad(loadState);
    }
  };

  const loadReportForType = (type, page = 1) => {
    switch (type) {
      case 'seals_assigned':
        return loadSealsReport(page);
      case 'summary':
        return loadSummaryReport();
      case 'seals_loaded':
        return loadSealsLoadedReport(page);
      case 'seals_tracing':
        return loadSealsTracingReport(page);
      case 'global_inventory':
        return loadGlobalInventoryReport();
      case 'client_seals_received':
        return loadClientSealsReceivedReport(page);
      case 'client_seals_assigned_depots':
        return loadClientSealsAssignedDepotsReport(page);
      case 'seals_used':
        return loadSealsUsedReport(page);
      case 'distributor_boxes_assigned':
        return loadDistributorBoxesAssignedReport(page);
      case 'distributor_inventory_status':
        return loadDistributorInventoryStatusReport(page);
      default:
        return loadSealsReport(page);
    }
  };

  const handleReportTypeChange = (e) => {
    const type = e.target.value;
    selectedReportTypeRef.current = type;
    setReportType(type);
    resetReportOutput();
    setSelectedCompanies([]);
    setSelectedDepots(role === 'operador' ? operadorAssignedDepotIds : []);
    setSelectedBatchId('');
    setBagFilter('');
    setSealFilter('');
    setSelectedTracingUserIds([]);
    setContainerNumber('');
    setSelectedLoadedUserIds([]);
    setSealsLoadedCompanyId('');
    setClientSealsStatus('');
  };

  const handleApplyFilters = () => {
    resetReportOutput();
    setReloadToken(prev => prev + 1);
  };

  const handleClearFilters = () => {
    setSelectedCompanies([]);
    setSelectedDepots([]);
    setSelectedBatchId('');
    setStartDate('');
    setEndDate('');
    setBagFilter('');
    setSealFilter('');
    setSelectedTracingUserIds([]);
    setContainerNumber('');
    setSelectedLoadedUserIds([]);
    setSealsLoadedCompanyId('');
    setClientSealsStatus('');
    resetReportOutput();
    setReloadToken(prev => prev + 1);
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(c => c !== companyId)
        : [...prev, companyId]
    );
  };

  const handleDepotToggle = (depotId) => {
    setSelectedDepots(prev => 
      prev.includes(depotId)
        ? prev.filter(d => d !== depotId)
        : [...prev, depotId]
    );
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      let response;
      let fileNamePrefix = 'report';

      if (reportType === 'seals_assigned') {
        let companyIds = selectedCompanies.length > 0 ? selectedCompanies.join(',') : null;
        let depotIds = selectedDepots.length > 0 ? selectedDepots.join(',') : null;
        if (role === 'operador') {
          companyIds = null;
          depotIds = operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0
            ? operadorAssignedDepotIds.join(',')
            : null;
        }
        response = await reportsAPI.exportSealsAssignedCSV({
          company_ids: companyIds,
          depot_ids: depotIds,
            start_date: startDate || null,
            end_date: endDate || null,
            upload_batch_id: selectedBatchId || null,
        });
        fileNamePrefix = 'seals-assigned';
      } else if (reportType === 'seals_loaded') {
        const params = {};
        if (selectedLoadedUserIds.length > 0) {
          params.user_ids = selectedLoadedUserIds.join(',');
        }
        if (role === 'operador') {
          if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
            params.depot_ids = operadorAssignedDepotIds.join(',');
          }
        } else if (sealsLoadedCompanyId) {
          params.company_id = sealsLoadedCompanyId;
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsLoadedCSV(params);
        fileNamePrefix = 'seals-loaded';
      } else if (reportType === 'seals_tracing') {
        const params = {};
        if (bagFilter) params.bag = bagFilter;
        if (sealFilter) params.seal = sealFilter;
        if (selectedTracingUserIds.length > 0) {
          params.user_ids = selectedTracingUserIds.join(',');
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsTracingCSV(params);
        fileNamePrefix = 'seals-tracing';
      } else if (reportType === 'global_inventory') {
        response = await reportsAPI.exportGlobalInventoryCSV({
          company_ids: selectedCompanies.length > 0 ? selectedCompanies.join(',') : null,
        });
        fileNamePrefix = 'global-inventory';
      } else if (reportType === 'client_seals_received') {
        {
          const params = {
            start_date: startDate || null,
            end_date: endDate || null,
          };
          if (selectedCompanies.length > 0) {
            params.company_ids = selectedCompanies.join(',');
          }
          if (selectedBatchId) {
            params.upload_batch_id = selectedBatchId;
          }
          if (role === 'operador') {
            if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
              params.depot_ids = operadorAssignedDepotIds.join(',');
            }
          } else if (selectedDepots.length > 0) {
            params.depot_ids = selectedDepots.join(',');
          }
          if (clientSealsStatus) {
            params.status = clientSealsStatus;
          }
          response = await reportsAPI.exportClientSealsReceivedCSV(params);
        }
        fileNamePrefix = 'client-seals-received';
      } else if (reportType === 'client_seals_assigned_depots') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedDepots.length === 1) {
          params.depot_id = selectedDepots[0];
        }
        response = await reportsAPI.exportClientSealsAssignedDepotsCSV(params);
        fileNamePrefix = 'client-seals-assigned-depots';
      } else if (reportType === 'seals_used') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedDepots.length === 1) {
          params.depot_id = selectedDepots[0];
        }
        if (containerNumber) {
          params.container_number = containerNumber;
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsUsedCSV(params);
        fileNamePrefix = 'seals-used';
      } else if (reportType === 'distributor_boxes_assigned') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedCompanies.length > 0) {
          params.company_ids = selectedCompanies.join(',');
        }
        response = await reportsAPI.exportDistributorBoxesAssignedCSV(params);
        fileNamePrefix = 'distributor-boxes-assigned';
      } else if (reportType === 'distributor_inventory_status') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedCompanies.length > 0) {
          params.company_ids = selectedCompanies.join(',');
        }
        response = await reportsAPI.exportDistributorInventoryStatusCSV(params);
        fileNamePrefix = 'distributor-inventory-status';
      } else if (reportType === 'summary') {
        // No export for summary currently
        toast.error('Summary report export is not available');
        setExporting(false);
        return;
      }

      const blob = response.data || response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileNamePrefix}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Error exporting CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      let response;
      let fileNamePrefix = 'report';

      if (reportType === 'seals_assigned') {
        let companyIds = selectedCompanies.length > 0 ? selectedCompanies.join(',') : null;
        let depotIds = selectedDepots.length > 0 ? selectedDepots.join(',') : null;
        if (role === 'operador') {
          companyIds = null;
          depotIds = operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0
            ? operadorAssignedDepotIds.join(',')
            : null;
        }
        response = await reportsAPI.exportSealsAssignedExcel({
          company_ids: companyIds,
          depot_ids: depotIds,
            start_date: startDate || null,
            end_date: endDate || null,
            upload_batch_id: selectedBatchId || null,
        });
        fileNamePrefix = 'seals-assigned';
      } else if (reportType === 'seals_loaded') {
        const params = {};
        if (selectedLoadedUserIds.length > 0) {
          params.user_ids = selectedLoadedUserIds.join(',');
        }
        if (role === 'operador') {
          if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
            params.depot_ids = operadorAssignedDepotIds.join(',');
          }
        } else if (sealsLoadedCompanyId) {
          params.company_id = sealsLoadedCompanyId;
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsLoadedExcel(params);
        fileNamePrefix = 'seals-loaded';
      } else if (reportType === 'seals_tracing') {
        const params = {};
        if (bagFilter) params.bag = bagFilter;
        if (sealFilter) params.seal = sealFilter;
        if (selectedTracingUserIds.length > 0) {
          params.user_ids = selectedTracingUserIds.join(',');
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsTracingExcel(params);
        fileNamePrefix = 'seals-tracing';
      } else if (reportType === 'global_inventory') {
        response = await reportsAPI.exportGlobalInventoryExcel({
          company_ids: selectedCompanies.length > 0 ? selectedCompanies.join(',') : null,
        });
        fileNamePrefix = 'global-inventory';
      } else if (reportType === 'client_seals_received') {
        {
          const params = {
            start_date: startDate || null,
            end_date: endDate || null,
          };
          if (selectedCompanies.length > 0) {
            params.company_ids = selectedCompanies.join(',');
          }
          if (selectedBatchId) {
            params.upload_batch_id = selectedBatchId;
          }
          if (role === 'operador') {
            if (operadorAssignedDepotIds && operadorAssignedDepotIds.length > 0) {
              params.depot_ids = operadorAssignedDepotIds.join(',');
            }
          } else if (selectedDepots.length > 0) {
            params.depot_ids = selectedDepots.join(',');
          }
          if (clientSealsStatus) {
            params.status = clientSealsStatus;
          }
          response = await reportsAPI.exportClientSealsReceivedExcel(params);
        }
        fileNamePrefix = 'client-seals-received';
      } else if (reportType === 'client_seals_assigned_depots') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (role === 'operador') {
          if (operadorAssignedDepotIds && operadorAssignedDepotIds.length === 1) {
            params.depot_id = operadorAssignedDepotIds[0];
          }
        } else if (selectedDepots.length === 1) {
          params.depot_id = selectedDepots[0];
        }
        response = await reportsAPI.exportClientSealsAssignedDepotsExcel(params);
        fileNamePrefix = 'client-seals-assigned-depots';
      } else if (reportType === 'seals_used') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (role === 'operador') {
          if (operadorAssignedDepotIds && operadorAssignedDepotIds.length === 1) {
            params.depot_id = operadorAssignedDepotIds[0];
          }
        } else if (selectedDepots.length === 1) {
          params.depot_id = selectedDepots[0];
        }
        if (containerNumber) {
          params.container_number = containerNumber;
        }
        if (selectedBatchId) params.upload_batch_id = selectedBatchId;
        response = await reportsAPI.exportSealsUsedExcel(params);
        fileNamePrefix = 'seals-used';
      } else if (reportType === 'distributor_boxes_assigned') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedCompanies.length > 0) {
          params.company_ids = selectedCompanies.join(',');
        }
        response = await reportsAPI.exportDistributorBoxesAssignedExcel(params);
        fileNamePrefix = 'distributor-boxes-assigned';
      } else if (reportType === 'distributor_inventory_status') {
        const params = {
          start_date: startDate || null,
          end_date: endDate || null,
        };
        if (selectedCompanies.length > 0) {
          params.company_ids = selectedCompanies.join(',');
        }
        response = await reportsAPI.exportDistributorInventoryStatusExcel(params);
        fileNamePrefix = 'distributor-inventory-status';
      } else if (reportType === 'summary') {
        toast.error('Summary report export is not available');
        setExporting(false);
        return;
      }

      const blob = response.data || response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileNamePrefix}-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Error exporting Excel');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(() => {
    if (reportType === 'seals_loaded') {
      return [
        { key: 'id', label: 'ID', width: '60px', sortable: true },
        { key: 'box', label: 'Box', sortable: true },
        { key: 'bag', label: 'Bag', sortable: true },
        { key: 'seal', label: 'Seal', sortable: true },
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'upload_batch_number', label: 'Batch', sortable: true },
        { key: 'uploaded_by_username', label: 'Loaded By', sortable: true },
        {
          key: 'status',
          label: 'Status',
          render: (value) => (
            <span className={`status-badge status-${value}`}>
              {value === 'assigned' ? 'Assigned' : 'Not Assigned'}
            </span>
          ),
          sortable: true,
        },
        { key: 'created_at', label: 'Created', sortable: true },
      ];
    }

    if (reportType === 'seals_tracing') {
      return [
        { key: 'bag', label: 'Bag', sortable: true },
        { key: 'seal', label: 'Seal', sortable: true },
        { key: 'event_type', label: 'Type', sortable: true },
        { key: 'event', label: 'Event', sortable: true },
        { key: 'message', label: 'Message' },
        { key: 'username', label: 'User', sortable: true },
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'upload_batch_number', label: 'Batch', sortable: true },
        { key: 'datetime', label: 'Date/Time', sortable: true },
      ];
    }

    if (reportType === 'client_seals_received') {
      return [
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'upload_batch_number', label: 'Batch', sortable: true },
        { key: 'batch_date', label: 'Date', sortable: true },
        { key: 'seals_count', label: 'Seals Count', sortable: true },
        { key: 'boxes_count', label: 'Boxes Count', sortable: true },
        { key: 'assigned_by_username', label: 'Assigned By', sortable: true },
      ];
    }

    if (reportType === 'client_seals_assigned_depots') {
      return [
        { key: 'box', label: 'Box', sortable: true },
        { key: 'bag', label: 'Bag', sortable: true },
        { key: 'seal', label: 'Seal', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'assigned_by_username', label: 'Assigned By', sortable: true },
        { key: 'assigned_by_user_type', label: 'User Type', sortable: true },
        { key: 'assigned_at', label: 'Assigned At', sortable: true },
      ];
    }

    if (reportType === 'seals_used') {
      return [
        { key: 'company_name', label: 'Client', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'box', label: 'Box', sortable: true },
        { key: 'bag', label: 'Bag', sortable: true },
        { key: 'seal', label: 'Seal', sortable: true },
        { key: 'container_number', label: 'Container Number', sortable: true },
        { key: 'upload_batch_number', label: 'Batch', sortable: true },
        { key: 'assigned_by_username', label: 'Assigned By', sortable: true },
        { key: 'assigned_at', label: 'Assigned At', sortable: true },
      ];
    }

    if (reportType === 'distributor_boxes_assigned') {
      return [
        { key: 'box', label: 'Box', sortable: true },
        { key: 'seals_count', label: 'Seals in Box', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'assigned_by_username', label: 'Assigned By', sortable: true },
        { key: 'assigned_by_user_type', label: 'User Type', sortable: true },
        { key: 'assigned_at', label: 'Assigned At', sortable: true },
      ];
    }

    if (reportType === 'distributor_inventory_status') {
      return [
        { key: 'box', label: 'Box', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'assigned_at', label: 'Last Change', sortable: true },
      ];
    }

    if (reportType === 'global_inventory') {
      return [
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'total_seals', label: 'Total Seals', sortable: true },
        { key: 'depot_name', label: 'Depot', sortable: true },
        { key: 'depot_total_seals', label: 'Depot Total Seals', sortable: true },
        { key: 'unassigned_boxes', label: 'Unassigned Boxes', sortable: true },
      ];
    }

    // Default: seals_assigned
    return [
      { key: 'id', label: 'ID', width: '60px', sortable: true },
      { key: 'box', label: 'Box', sortable: true },
      { key: 'company_name', label: 'Company', sortable: true },
      { key: 'depot_name', label: 'Depot', sortable: true },
      {
        key: 'status',
        label: 'Status',
        render: (value) => (
          <span className={`status-badge status-${value}`}>
            {value === 'assigned' ? 'Assigned' : 'Not Assigned'}
          </span>
        ),
        sortable: true,
      },
      { key: 'upload_batch_number', label: 'Batch', sortable: true },
      { key: 'assigned_by_username', label: 'Assigned By', sortable: true },
      { key: 'assigned_by_user_type', label: 'User Type', sortable: true },
      { key: 'assigned_at', label: 'Assigned At', sortable: true },
      { key: 'created_at', label: 'Created', sortable: true },
    ];
    
  }, [reportType]);

  const isPaginatedReport = [
    'seals_assigned',
    'seals_loaded',
    'seals_tracing',
    'client_seals_received',
    'client_seals_assigned_depots',
    'seals_used',
    'distributor_boxes_assigned',
    'distributor_inventory_status',
  ].includes(reportType);
  const totalPages = isPaginatedReport ? Math.ceil(totalCount / itemsPerPage) || 1 : 1;

  const hasDataForExport = reportType === 'global_inventory'
    ? (globalInventory && globalInventory.length > 0)
    : reportData.length > 0;

  const canExportExcel = role && ['gods_eye', 'manager', 'cliente'].includes(role);

  const handlePageChangeFromTable = (page) => {
    setCurrentPage(page);
    loadReportForType(reportType, page);
  };

  const flattenedGlobalInventory = useMemo(() => {
    if (!globalInventory || globalInventory.length === 0) return [];

    const rows = [];
    globalInventory.forEach((company) => {
      const depotsRows = company.depots && company.depots.length > 0
        ? company.depots
        : [{ depot_id: null, depot_name: '', total_seals: '' }];

      depotsRows.forEach((depotRow, index) => {
        rows.push({
          id: `${company.company_id}-${depotRow.depot_id || 'none'}-${index}`,
          company_name: company.company_name,
          total_seals: company.total_seals,
          depot_name: depotRow.depot_name || '-',
          depot_total_seals: depotRow.total_seals !== '' ? depotRow.total_seals : '-',
          unassigned_boxes: company.unassigned_boxes,
        });
      });
    });

    return rows;
  }, [globalInventory]);

  const currentReportLabel = useMemo(() => {
    const currentOption = availableReportOptions.find((opt) => opt.value === reportType);
    if (currentOption) return currentOption.label;
    if (reportType === 'summary') return 'Summary Overview';
    return 'Reports';
  }, [availableReportOptions, reportType]);

  const defaultSortKey = useMemo(() => {
    const boxSortedReports = [
      'seals_assigned',
      'seals_loaded',
      'client_seals_received',
      'client_seals_assigned_depots',
      'seals_used',
      'distributor_boxes_assigned',
      'distributor_inventory_status',
    ];
    return boxSortedReports.includes(reportType) ? 'box' : null;
  }, [reportType]);

  return (
    <div className="container">
      <h1 className="page-title">
        {currentReportLabel}
      </h1>

      <div className="report-type-selector" style={{ marginBottom: '1rem' }}>
        <label style={{ marginRight: '0.5rem', fontWeight: 500 }}>Report Type:</label>
        <select value={reportType} onChange={handleReportTypeChange}>
          {availableReportOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* FILTERS SECTION (aligned with Audit Logs / Notifications layout) */}
      <div className="filter-section">
        <h3>Filter Report</h3>

        <div className="filters-section">
          {/* Companies Filter */}
          {[
            'seals_assigned',
            'global_inventory',
            'client_seals_received',
            'distributor_boxes_assigned',
            'distributor_inventory_status',
          ].includes(reportType) && role !== 'operador' && (
            <div className="filter-group">
              <label className="filter-label">Companies</label>
              <div className="filter-checkboxes">
                {companies.length === 0 ? (
                  <p className="text-muted">No companies available</p>
                ) : (
                  companies.map((company) => (
                    <label key={company.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedCompanies.includes(company.id)}
                        onChange={() => handleCompanyToggle(company.id)}
                      />
                      <span>{company.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Upload Batch Filter - show for cliente/manager/gods_eye when viewing seals_assigned */}
          {['seals_assigned', 'client_seals_received'].includes(reportType) && ['cliente','manager','gods_eye'].includes(role) && (
            <div className="filter-group">
              <label className="filter-label">Upload Batch</label>
              <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
                <option value="">All Batches</option>
                {batches.map((b) => (
                  <option key={b.upload_batch_id} value={b.upload_batch_id}>
                    {b.label} ({b.device_count})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Depots Filter - used for Seals Assigned, Client Seals Received, Client Seals Assigned to Depots, and Seals Used */}
          {(reportType === 'seals_assigned' ||
            reportType === 'client_seals_received' ||
            reportType === 'client_seals_assigned_depots' ||
            reportType === 'seals_used') && role !== 'operador' && (
            <div className="filter-group">
              <label className="filter-label">Depots</label>
              <div className="filter-checkboxes">
                {visibleDepots.length === 0 ? (
                  <p className="text-muted">No depots available</p>
                ) : (
                  visibleDepots.map((depot) => (
                    <label key={depot.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedDepots.includes(depot.id)}
                        onChange={() => handleDepotToggle(depot.id)}
                      />
                      <span>{depot.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Date Range Filter - only for reports that support it */}
          {[
            'seals_assigned',
            'summary',
            'client_seals_received',
            'client_seals_assigned_depots',
            'seals_used',
            'distributor_boxes_assigned',
            'distributor_inventory_status',
          ].includes(reportType) && (
            <div className="filter-group">
              <label className="filter-label">Date Range</label>
              <div className="date-inputs">
                <div>
                  <label htmlFor="start-date">From</label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="end-date">To</label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                {reportType === 'client_seals_received' && (
                  <div>
                    <label htmlFor="client-seals-status">Status</label>
                    <select
                      id="client-seals-status"
                      value={clientSealsStatus}
                      onChange={(e) => setClientSealsStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="available">Available</option>
                      <option value="awaiting_container">Awaiting Container</option>
                      <option value="in_use">In Use</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {reportType === 'seals_loaded' && (
            <>
              <div className="filter-group">
                <label className="filter-label">Loaded By (User)</label>
                <select
                  multiple
                  value={selectedLoadedUserIds}
                  onChange={(e) => {
                    const options = Array.from(e.target.selectedOptions);
                    setSelectedLoadedUserIds(options.map((opt) => opt.value));
                  }}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username}
                    </option>
                  ))}
                </select>
              </div>
              {role !== 'operador' && (
                <div className="filter-group">
                  <label className="filter-label">Company</label>
                  <select
                    value={sealsLoadedCompanyId}
                    onChange={(e) => setSealsLoadedCompanyId(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {reportType === 'seals_tracing' && (
            <div className="filter-group">
              <label className="filter-label">Tracing Filters</label>
              <div className="tracing-filters-inline">
                <div>
                  <label htmlFor="bag-filter">Bag</label>
                  <input
                    id="bag-filter"
                    type="text"
                    placeholder="Bag ID"
                    value={bagFilter}
                    onChange={(e) => setBagFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="seal-filter">Seal</label>
                  <input
                    id="seal-filter"
                    type="text"
                    placeholder="Seal ID"
                    value={sealFilter}
                    onChange={(e) => setSealFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="user-id-filter">Users</label>
                  <select
                    id="user-id-filter"
                    multiple
                    value={selectedTracingUserIds}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions);
                      setSelectedTracingUserIds(options.map((opt) => opt.value));
                    }}
                  >
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {reportType === 'seals_used' && (
            <div className="filter-group">
              <label className="filter-label">Container</label>
              <input
                type="text"
                placeholder="Container Number"
                value={containerNumber}
                onChange={(e) => setContainerNumber(e.target.value)}
              />
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="filter-actions">
          <button 
            className="btn btn-primary"
            onClick={handleApplyFilters}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleClearFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* EXPORT SECTION */}
      {reportType !== 'summary' && (
        <div className="export-panel">
          <h3>Export</h3>
          <div className="export-buttons">
            <button 
              className="btn btn-success"
              onClick={handleExportCSV}
              disabled={exporting || !hasDataForExport}
            >
              <ExportCsvIcon />
              <span style={{ marginLeft: '0.5rem' }}>Export CSV</span>
            </button>
            {canExportExcel && (
              <button 
                className="btn btn-success"
                onClick={handleExportExcel}
                disabled={exporting || !hasDataForExport}
              >
                <ExportExcelIcon />
                <span style={{ marginLeft: '0.5rem' }}>Export Excel</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* REPORT INFO */}
      <div className="report-info">
        {reportType === 'seals_assigned' ? (
          <>
            <div className="info-item">
              <span className="info-label">Total Records:</span>
              <span className="info-value">{totalCount}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Page:</span>
              <span className="info-value">{currentPage} / {totalPages}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Showing:</span>
              <span className="info-value">{reportData.length} of {totalCount}</span>
            </div>
          </>
        ) : (
          summaryReport && (
            <>
              <div className="info-item">
                <span className="info-label">Report Date:</span>
                <span className="info-value">{summaryReport.report_date}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Period:</span>
                <span className="info-value">
                  {summaryReport.period?.start_date || 'N/A'} - {summaryReport.period?.end_date || 'N/A'}
                </span>
              </div>
            </>
          )
        )}
      </div>

      {/* DATA / SUMMARY SECTION */}
      {reportType === 'summary' ? (
        loading ? (
          <div className="loading-state">
            <p>Loading summary report...</p>
          </div>
        ) : !summaryReport ? (
          <div className="empty-state">
            <p>No summary data available for the selected filters</p>
          </div>
        ) : (
          <div className="summary-grid">
            <div className="summary-card">
              <h3>Seals</h3>
              <p>Total: {summaryReport.seals?.total}</p>
            </div>
            <div className="summary-card">
              <h3>Box Assignments</h3>
              <p>Total: {summaryReport.assignments?.total}</p>
              <p>Assigned: {summaryReport.assignments?.assigned}</p>
              <p>Not Assigned: {summaryReport.assignments?.not_assigned}</p>
            </div>
            <div className="summary-card">
              <h3>Notifications</h3>
              <p>Total: {summaryReport.notifications?.total}</p>
              <p>Unread: {summaryReport.notifications?.unread}</p>
              <p>Read: {summaryReport.notifications?.read}</p>
            </div>
          </div>
        )
      ) : reportType === 'global_inventory' ? (
        loading ? (
          <div className="loading-state">
            <p>Loading global inventory...</p>
          </div>
        ) : !globalInventory || globalInventory.length === 0 ? (
          <div className="empty-state">
            <p>No inventory data available</p>
          </div>
        ) : (
          <div>
            <DataTable
              key={`global-inventory-${reportType}`}
              data={flattenedGlobalInventory}
              columns={columns}
              itemsPerPage={itemsPerPage}
              serverPagination
              totalCount={totalCount}
              currentPageExternal={currentPage}
              onPageChangeExternal={handlePageChangeFromTable}
              title={currentReportLabel}
              defaultSortKey={defaultSortKey}
            />
          </div>
        )
      ) : (
        <>
          {loading ? (
            <div className="loading-state">
              <p>Loading report data...</p>
            </div>
          ) : reportData.length === 0 ? (
            <div className="empty-state">
              <p>No data available for the selected filters</p>
            </div>
          ) : (
            <div>
              <DataTable
                key={`report-table-${reportType}`}
                data={reportData}
                columns={columns}
                itemsPerPage={itemsPerPage}
                serverPagination={isPaginatedReport}
                totalCount={isPaginatedReport ? totalCount : null}
                currentPageExternal={isPaginatedReport ? currentPage : null}
                onPageChangeExternal={isPaginatedReport ? handlePageChangeFromTable : null}
                title={currentReportLabel}
                defaultSortKey={defaultSortKey}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

